// AU18 (docs/build-briefs/AU18-location-audit-log.md). The Audit log screen's decisions as pure
// functions: what a filter set asks the server, the day headings, the initials, the short lines and
// the link each row may carry. No DOM and no session import, so a plain node:test file can import it
// (test/ui-audit-log-model.test.ts) and ui/src/auditLog.ts draws from it.

import type { AuditLogEntryDto, AuditLogLinkDto } from "./api.ts";
import {
  CHANGE_HISTORY_RANGE_CHIPS,
  shortDate,
  type ChangeHistoryRangeId,
} from "./changeHistoryModel.ts";
import { formatRoute } from "./screenRoute.ts";
import type { GateExportFormat } from "./exportMenuModel.ts";

export const AUDIT_LOG_RANGE_CHIPS = CHANGE_HISTORY_RANGE_CHIPS;
export type AuditLogRangeId = ChangeHistoryRangeId;

export const AUDIT_LOG_PAGE_SIZE = 50;

export interface AuditLogFilters {
  range: AuditLogRangeId;
  /** Only read when range is "custom": YYYY-MM-DD in the location's time zone. */
  from: string;
  to: string;
  /** "" for everyone, else a person id the server listed. */
  person: string;
  /** "" for every type, else an action id. */
  type: string;
  /** "" for the server's choice (the device's location), else a location id. */
  locationId: string;
}

export function defaultAuditFilters(): AuditLogFilters {
  return { range: "last_7_days", from: "", to: "", person: "", type: "", locationId: "" };
}

/** The query string the server reads. A custom range with a missing date asks for the default range
 * instead, so a half-typed pair never reaches the server as an error. */
export function auditLogQuery(
  filters: AuditLogFilters,
  extra: { cursor?: string; csv?: boolean; format?: GateExportFormat } = {},
): string {
  const params = new URLSearchParams();
  if (filters.locationId !== "") params.set("locationId", filters.locationId);
  if (filters.range === "custom") {
    if (filters.from !== "" && filters.to !== "") {
      params.set("from", filters.from);
      params.set("to", filters.to);
    }
  } else {
    params.set("range", filters.range);
  }
  if (filters.person !== "") params.set("person", filters.person);
  if (filters.type !== "") params.set("type", filters.type);
  if (extra.format !== undefined || extra.csv === true) {
    params.set("format", extra.format ?? "csv");
  } else {
    params.set("limit", String(AUDIT_LOG_PAGE_SIZE));
    if (extra.cursor !== undefined) params.set("cursor", extra.cursor);
  }
  return params.toString();
}

/** True when a person or type filter is on (the date range always applies). */
export function auditFiltersActive(filters: AuditLogFilters): boolean {
  return filters.person !== "" || filters.type !== "";
}

function addDays(dateKey: string, days: number): string {
  const [y, m, d] = dateKey.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

export interface AuditDayGroup {
  dayKey: string;
  heading: string;
  /** "Tue Oct 6 · 6 events" */
  sub: string;
  entries: AuditLogEntryDto[];
}

/** Day headings: Today and Yesterday by name, every other day by its own date. Entries keep the order
 * the server sent them (newest first). */
export function groupAuditByDay(
  entries: readonly AuditLogEntryDto[],
  todayLocal: string,
): AuditDayGroup[] {
  const groups: AuditDayGroup[] = [];
  for (const entry of entries) {
    const last = groups[groups.length - 1];
    if (last !== undefined && last.dayKey === entry.dayKey) {
      last.entries.push(entry);
      continue;
    }
    const heading =
      entry.dayKey === todayLocal
        ? "Today"
        : entry.dayKey === addDays(todayLocal, -1)
          ? "Yesterday"
          : entry.dayLabel;
    groups.push({ dayKey: entry.dayKey, heading, sub: "", entries: [entry] });
  }
  for (const group of groups) {
    const first = group.entries[0];
    const count = group.entries.length;
    const words = `${count} ${count === 1 ? "event" : "events"}`;
    group.sub =
      group.heading === (first?.dayLabel ?? group.dayKey)
        ? words
        : `${first?.dayLabel ?? group.dayKey} · ${words}`;
  }
  return groups;
}

/** Two letters for the avatar. Support actors read "SS". */
export function initialsOf(name: string, kind: AuditLogEntryDto["actor"]["kind"]): string {
  if (kind === "support") return "SS";
  const words = name
    .trim()
    .split(/\s+/)
    .filter((w) => w !== "");
  if (words.length === 0) return "?";
  if (words.length === 1) return (words[0] as string).slice(0, 1).toUpperCase();
  return `${(words[0] as string).slice(0, 1)}${(words[words.length - 1] as string).slice(0, 1)}`.toUpperCase();
}

export function showingLine(shown: number, more: boolean): string {
  if (more) return `Showing ${shown} events`;
  return shown === 1 ? "1 event" : `${shown} events`;
}

/** The hash a row's link opens, or undefined. Only screens that exist are linked: an open ticket, the
 * void history for a business date, the closeout for a business date, one count. */
export function linkHref(link: AuditLogLinkDto | null): string | undefined {
  if (link === null) return undefined;
  switch (link.kind) {
    case "ticket":
      return formatRoute({ kind: "ticket", orderId: link.value });
    case "void-history":
      return formatRoute({ kind: "void-history", date: link.value });
    case "closeout":
      return formatRoute({ kind: "closeout", date: link.value });
    case "count":
      return formatRoute({ kind: "view", view: "counts", countSessionId: link.value });
  }
}

/** The word on the link, for the screen reader. */
export function linkLabel(link: AuditLogLinkDto): string {
  switch (link.kind) {
    case "ticket":
      return "Open ticket";
    case "void-history":
      return "Open void history";
    case "closeout":
      return "Open closeout";
    case "count":
      return "Open count";
  }
}

/** The date span line under the filters: "Oct 7 to Oct 13 - Eastern Time". */
export function spanLine(page: { from?: string; to?: string; timeZoneName?: string }): string {
  if (page.from === undefined || page.to === undefined) return "";
  const base = `${shortDate(page.from)} to ${shortDate(page.to)}`;
  return page.timeZoneName === undefined || page.timeZoneName === "" || page.timeZoneName === "UTC"
    ? base
    : `${base} · ${page.timeZoneName}`;
}

/** The download name, which carries the location so two locations' files do not collide. */
export function downloadName(
  page: { from?: string; to?: string; locationName?: string },
  extension: "csv" | "xlsx" | "pdf" = "csv",
): string {
  const locationSlug = (page.locationName ?? "location")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `audit-log-${locationSlug === "" ? "location" : locationSlug}-${page.from ?? ""}-to-${page.to ?? ""}.${extension}`;
}
